/**
 * LEOS Mission Ready UI Module
 * Handles all UI-related methods and interactions
 */

export class UIModule {
    constructor(mainInterface) {
        this.mainInterface = mainInterface;
    }

    toggleSidebar() {
        const sidebar = document.querySelector('.sidebar-navigation');
        const toggleIcon = document.querySelector('.toggle-icon');
        const isCollapsed = sidebar.classList.contains('collapsed');
        
        if (isCollapsed) {
            // Expand sidebar
            sidebar.classList.remove('collapsed');
            if (toggleIcon) {
                toggleIcon.innerHTML = '<img src="assets/chevron-left.svg" alt="Collapse" style="width: 16px; height: 16px; filter: brightness(0) saturate(100%) invert(64%) sepia(8%) saturate(507%) hue-rotate(184deg) brightness(91%) contrast(87%);" />';
            }
        } else {
            // Collapse sidebar
            sidebar.classList.add('collapsed');
            if (toggleIcon) {
                toggleIcon.innerHTML = '<img src="assets/chevron-right.svg" alt="Expand" style="width: 16px; height: 16px; filter: brightness(0) saturate(100%) invert(64%) sepia(8%) saturate(507%) hue-rotate(184deg) brightness(91%) contrast(87%);" />';
            }
        }
        
        // Trigger canvas resize after transition
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
            // Also trigger a manual resize of the canvas if needed
            const canvas = document.getElementById('renderCanvas');
            if (canvas && window.engine) {
                window.engine.resize();
            }
        }, 350);
    }

    toggleFullscreen() {
        const visualizationContainer = document.querySelector('.visualization-container');
        const canvas = document.getElementById('renderCanvas');
        const fullscreenBtn = document.getElementById('fullscreen-btn');
        
        if (!document.fullscreenElement) {
            // Enter fullscreen
            if (visualizationContainer.requestFullscreen) {
                visualizationContainer.requestFullscreen();
            } else if (visualizationContainer.webkitRequestFullscreen) {
                visualizationContainer.webkitRequestFullscreen();
            } else if (visualizationContainer.msRequestFullscreen) {
                visualizationContainer.msRequestFullscreen();
            }
            
            // Update button icon
            fullscreenBtn.innerHTML = '<img src="assets/compress.svg" alt="Exit Fullscreen" style="width: 20px; height: 20px; filter: brightness(0) saturate(100%) invert(73%) sepia(77%) saturate(1462%) hue-rotate(166deg) brightness(104%) contrast(103%);" />';
            fullscreenBtn.title = 'Exit Fullscreen';
            
            // Ensure canvas fills the fullscreen
            visualizationContainer.style.padding = '0';
            if (canvas) {
                canvas.style.width = '100vw';
                canvas.style.height = '100vh';
            }
        } else {
            // Exit fullscreen
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
            
            // Update button icon
            fullscreenBtn.innerHTML = '<img src="assets/expand.svg" alt="Fullscreen" style="width: 20px; height: 20px; filter: brightness(0) saturate(100%) invert(73%) sepia(77%) saturate(1462%) hue-rotate(166deg) brightness(104%) contrast(103%);" />';
            fullscreenBtn.title = 'Toggle Fullscreen';
            
            // Reset canvas size
            visualizationContainer.style.padding = '';
            if (canvas) {
                canvas.style.width = '100%';
                canvas.style.height = '100%';
            }
        }
        
        // Resize engine after transition
        setTimeout(() => {
            if (window.app && window.app.engine) {
                window.app.engine.resize();
            }
        }, 100);
    }

    hideOldUI() {
        // Don't show CAPCOM button yet - wait for simulation to load
        const capcomBtn = document.getElementById('capcom-kelly-btn');
        if (capcomBtn) {
            capcomBtn.style.display = 'none';
        }
        
        // Make sure mission dashboard is available but hidden
        const missionDashboard = document.getElementById('mission-dashboard');
        if (missionDashboard) {
            missionDashboard.classList.add('hidden');
            missionDashboard.style.display = '';  // Remove any inline display:none
        }
        
        // Hide old floating UI elements
        const oldUIElements = [
            'sda-toggle-btn', 
            'simulation-settings-btn',
            'add-tle-button',
            'telemetry-dashboard-btn',
            'data-source-btn',
            'leos-dial',
            'kelcodes',
            'time-display-container',
            'time-display',
            'welcome-modal',
            'loading-screen',
            'control-dock-container',
            'control-dock',
            'minimized-dock',
            'learning-btn',
            'support-btn',
            'dock-minimize-btn',
            'cyberrts-logo-link'
        ];
        
        oldUIElements.forEach(id => {
            const element = document.getElementById(id);
            if (element) element.style.display = 'none';
        });
    }

    showStatInfo(statType) {
        const info = {
            satellites: {
                title: 'Active Satellites',
                content: 'Currently tracking BULLDOG and CyberRTS satellites in low Earth orbit. These satellites provide real-time telemetry data and communication capabilities.'
            },
            ground: {
                title: 'Ground Station Network',
                content: 'Our network includes 5 strategic ground stations: Canberra DSN Site, Vandenberg CA, Wallops Island VA, Mauna Kea Station, and Diego Garcia, providing global coverage.'
            },
            data: {
                title: 'Data Rate',
                content: 'Real-time telemetry data throughput from all active satellites. Rate increases when satellites are in line-of-sight with ground stations.'
            },
            missions: {
                title: 'Active Missions',
                content: 'Operational missions currently being executed. Includes satellite deployments, data collection campaigns, and orbital maneuvers.'
            }
        };
        
        const statInfo = info[statType];
        if (statInfo) {
            // Create a simple tooltip
            const tooltip = document.createElement('div');
            tooltip.style.cssText = `
                position: fixed;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                background: rgba(15, 23, 42, 0.98);
                border: 1px solid rgba(0, 212, 255, 0.3);
                border-radius: 12px;
                padding: 24px;
                max-width: 400px;
                z-index: 10000;
                box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
            `;
            
            tooltip.innerHTML = `
                <h3 style="color: #00d4ff; margin: 0 0 12px 0; font-size: 18px;">${statInfo.title}</h3>
                <p style="color: #e2e8f0; margin: 0 0 16px 0; line-height: 1.5;">${statInfo.content}</p>
                <button onclick="this.parentElement.remove()" style="
                    background: rgba(0, 153, 255, 0.2);
                    border: 1px solid rgba(0, 153, 255, 0.3);
                    border-radius: 6px;
                    padding: 8px 16px;
                    color: #00d4ff;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 500;
                ">Close</button>
            `;
            
            document.body.appendChild(tooltip);
            
            // Auto-remove after 5 seconds
            setTimeout(() => {
                if (tooltip.parentElement) {
                    tooltip.remove();
                }
            }, 5000);
        }
    }

    showUpgradeModal(featureName) {
        const modal = document.createElement('div');
        modal.className = 'upgrade-modal';
        modal.innerHTML = `
            <div class="upgrade-content">
                <h3>Upgrade Required</h3>
                <p>${featureName} is available in the Pro version.</p>
                <button onclick="this.closest('.upgrade-modal').remove()">Close</button>
            </div>
        `;
        document.body.appendChild(modal);
    }

    showUserProfile() {
        if (this.mainInterface.userProfileOverlay) {
            this.mainInterface.userProfileOverlay.show();
        }
    }

    moveCanvasToContainer() {
        const canvas = document.getElementById('renderCanvas');
        const canvasContainer = document.getElementById('canvas-container');
        
        if (canvas && canvasContainer) {
            // Make sure canvas is visible before moving it
            canvas.style.display = 'block';
            canvas.style.visibility = 'visible';
            canvas.style.opacity = '1';
            
            canvasContainer.appendChild(canvas);
            canvas.style.width = '100%';
            canvas.style.height = '100%';
            
            // Force a resize to ensure proper rendering
            if (window.app && window.app.engine) {
                window.app.engine.resize();
                
                // Ensure render loop is running
                if (!window.app.engine.isDisposed) {
                    window.app.engine.runRenderLoop(() => {
                        if (window.app.scene) {
                            window.app.scene.render();
                        }
                    });
                }
            }
            
            // Trigger resize event
            setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
            }, 100);
        }
    }

    initializeDraggableTiles() {
        const tiles = document.querySelectorAll('.mission-dashboard-tile');
        
        tiles.forEach(tile => {
            let isDragging = false;
            let currentX;
            let currentY;
            let initialX;
            let initialY;
            let xOffset = 0;
            let yOffset = 0;

            tile.addEventListener('mousedown', dragStart);
            tile.addEventListener('mouseup', dragEnd);
            tile.addEventListener('mousemove', drag);

            function dragStart(e) {
                if (e.target.tagName === 'BUTTON' || e.target.tagName === 'INPUT') {
                    return;
                }
                
                initialX = e.clientX - xOffset;
                initialY = e.clientY - yOffset;

                if (e.target === tile) {
                    isDragging = true;
                    tile.style.position = 'relative';
                    tile.style.zIndex = '1000';
                }
            }

            function dragEnd(e) {
                initialX = currentX;
                initialY = currentY;

                isDragging = false;
                tile.style.zIndex = '10';
            }

            function drag(e) {
                if (e.preventDefault) {
                    e.preventDefault();
                }

                if (isDragging) {
                    currentX = e.clientX - initialX;
                    currentY = e.clientY - initialY;

                    xOffset = currentX;
                    yOffset = currentY;

                    tile.style.transform = `translate(${currentX}px, ${currentY}px)`;
                }
            }
        });
    }

    setupSimulationTimeControls() {
        const simTimeDisplay = document.getElementById('simulation-time-display');
        const simControlsPanel = document.getElementById('sim-time-controls');
        const speedBtns = Array.from(document.querySelectorAll('#sim-time-controls .time-btn.speed-btn'));
        
        let controlsVisible = false;
        
        if (!simTimeDisplay || !simControlsPanel) return;
        
        // Update button states
        function updateSpeedBtns() {
            if (window.simState) {
                speedBtns.forEach(btn => {
                    const mult = parseFloat(btn.getAttribute('data-mult'));
                    if (Math.abs(window.simState.timeMultiplier - mult) < 0.001) {
                        btn.classList.add('active');
                    } else {
                        btn.classList.remove('active');
                    }
                });
            }
        }
        
        // Show/hide controls panel on time display click
        simTimeDisplay.addEventListener('click', (e) => {
            if (e.target.classList.contains('time-btn')) return;
            controlsVisible = !controlsVisible;
            simControlsPanel.classList.toggle('hidden', !controlsVisible);
            updateSpeedBtns();
        });
        
        // Speed button clicks
        speedBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                const mult = parseFloat(btn.getAttribute('data-mult'));
                if (window.simState && window.simState.timeMultiplier !== mult) {
                    window.simState.timeMultiplier = mult;
                    window.simState.lastTimeMultiplier = mult;
                }
                updateSpeedBtns();
            });
        });
        
        // Hide controls if clicking outside
        document.addEventListener('mousedown', (e) => {
            if (controlsVisible && 
                !simTimeDisplay.contains(e.target) && 
                !simControlsPanel.contains(e.target)) {
                simControlsPanel.classList.add('hidden');
                controlsVisible = false;
            }
        });
    }

    updateMissionStats() {
        // This method is called by startStatsUpdate in the main interface
        // Implementation depends on your specific stats update logic
    }

    // Legacy integration methods
    toggleSDA() {
        const sdaToggleBtn = document.getElementById('sda-toggle-btn');
        if (sdaToggleBtn) {
            sdaToggleBtn.click();
        }
    }

    addTLE() {
        const addTLEBtn = document.getElementById('add-tle-button');
        if (addTLEBtn) {
            addTLEBtn.click();
        }
    }

    openDataManager() {
        // Trigger Ctrl+D for data source manager
        const event = new KeyboardEvent('keydown', {
            key: 'd',
            ctrlKey: true,
            bubbles: true
        });
        document.dispatchEvent(event);
    }
}